import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EnquiryModal from '../components/EnquiryModal';
import ReviewsList from '../components/ReviewsList';
import SupplierHero from '../components/supplier/SupplierHero';
import SupplierAboutTab from '../components/supplier/SupplierAboutTab';
import SupplierProductsTab from '../components/supplier/SupplierProductsTab';
import SupplierCertificatesTab from '../components/supplier/SupplierCertificatesTab';
import SupplierContactTab from '../components/supplier/SupplierContactTab';
import RelatedVendors from '../components/supplier/RelatedVendors';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { api } from '../lib/api';
import useSEO from '../lib/useSEO';
import { ChevronRight, Info, Package, Phone, ShieldCheck, Star } from 'lucide-react';

/** Composer for the public supplier profile page. Tab content lives under
 * `/components/supplier/`. This file only owns data-fetching state and the
 * currently active tab. */
const SupplierProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [tab, setTab] = useState('about');
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    setSupplier(null); setProducts([]); setNotFound(false); setTab('about'); setCertificates([]);
    api.getSupplier(id).then(async (s) => {
      setSupplier(s);
      const prods = await api.getProducts({ supplier_id: s.id, limit: 60 });
      setProducts(prods.items || []);
      try {
        const certs = await api.getSupplierCertificates(s.id);
        setCertificates(certs || []);
      } catch (e) { console.warn('Failed to load certs', e); }
    }).catch(() => setNotFound(true));
  }, [id]);

  // Canonicalise the URL to the SEO-friendly slug form (/vendor/{slug}).
  // Anyone landing via the raw UUID or the /supplier/ alias is redirected so
  // the address bar + canonical link always show the vendor name.
  useEffect(() => {
    if (supplier?.slug) {
      const canonical = `/vendor/${supplier.slug}`;
      if (location.pathname !== canonical) navigate(canonical, { replace: true });
    }
  }, [supplier, location.pathname, navigate]);

  // ---------- SEO ----------
  // Title pattern: "{Company} – {City} | Verified B2B Vendor on TelanganaMart"
  // Description: first ~160 chars of vendor description, falling back to a
  //   composed line using categories + city + business_type.
  // JSON-LD: LocalBusiness + BreadcrumbList — both improve Google rich-results.
  const seoTitle = supplier
    ? `${supplier.name}${supplier.city ? ` — ${supplier.city}` : ''} | Verified B2B Vendor`
    : undefined;
  const cats = Array.isArray(supplier?.categories) ? supplier.categories.filter(Boolean) : [];
  const seoDescription = supplier
    ? (
        (supplier.description || '').slice(0, 160) ||
        `${supplier.name} is a${supplier.claimed ? ' verified' : ''} ${supplier.business_type || 'B2B supplier'} based in ${supplier.city || 'Telangana'}${cats.length ? `, dealing in ${cats.slice(0, 3).join(', ')}` : ''}. Contact for quotes on TelanganaMart.`
      )
    : undefined;
  const seoKeywords = supplier
    ? [supplier.name, supplier.city, supplier.business_type, ...cats, 'B2B', 'TelanganaMart']
        .filter(Boolean).join(', ')
    : undefined;
  const jsonLd = useMemo(() => {
    if (!supplier) return null;
    const origin = (typeof window !== 'undefined' && window.location.origin) || '';
    const profileUrl = `${origin}/vendor/${supplier.slug || supplier.id}`;
    const localBusiness = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: supplier.name,
      description: supplier.description || undefined,
      url: profileUrl,
      image: supplier.logo_url || undefined,
      telephone: supplier.mobile_revealed ? `+91${supplier.mobile}` : undefined,
      email: supplier.email || undefined,
      sameAs: supplier.website ? [supplier.website] : undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: supplier.address || undefined,
        addressLocality: supplier.city || undefined,
        addressRegion: 'Telangana',
        addressCountry: 'IN',
      },
      geo: (typeof supplier.latitude === 'number' && typeof supplier.longitude === 'number')
        ? { '@type': 'GeoCoordinates', latitude: supplier.latitude, longitude: supplier.longitude }
        : undefined,
      foundingDate: supplier.established ? String(supplier.established) : undefined,
      aggregateRating: (supplier.rating && supplier.rating > 0)
        ? { '@type': 'AggregateRating', ratingValue: supplier.rating, bestRating: 5, reviewCount: products.length || 1 }
        : undefined,
    };
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: 'Vendors', item: `${origin}/directory` },
        { '@type': 'ListItem', position: 3, name: supplier.name, item: profileUrl },
      ],
    };
    return [localBusiness, breadcrumb];
  }, [supplier, products.length]);

  useSEO({
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    image: supplier?.logo_url,
    canonical: supplier && typeof window !== 'undefined'
      ? `${window.location.origin}/vendor/${supplier.slug || supplier.id}`
      : undefined,
    jsonLd,
  });

  if (notFound) {
    return (
      <div>
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold">Vendor not found</h1>
          <Link to="/" className="text-[#2c54b2] hover:underline mt-4 inline-block">Go back home</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!supplier) {
    return <div><Header /><div className="max-w-7xl mx-auto px-4 py-20 text-center text-gray-500">Loading vendor...</div><Footer /></div>;
  }

  const openEnquiry = () => setEnquiryOpen(true);
  const goToCerts = () => setTab('certificates');
  const goToContact = () => setTab('contact');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-1 text-xs text-gray-600">
          <Link to="/" className="hover:text-[#2c54b2]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span>Vendors</span>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-900 font-medium">{supplier.name}</span>
        </div>
      </div>

      <SupplierHero
        supplier={supplier}
        onEnquire={openEnquiry}
        onSwitchToContact={goToContact}
        onClaimed={(updated) => setSupplier(updated)}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          {/* Horizontal scroller so the row never wraps / overlaps on mobile.
              `flex-nowrap` keeps Shadcn triggers in one row; the parent owns
              overflow so the focus ring isn't clipped. `whitespace-nowrap` on
              each trigger prevents the label from breaking to two lines. */}
          <div className="-mx-4 px-4 overflow-x-auto scrollbar-hide" data-testid="vendor-tabs-scroller">
            <TabsList className="inline-flex w-max flex-nowrap bg-white border border-gray-200 rounded-md p-1 h-auto">
              <TabsTrigger value="about" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-5 py-2 whitespace-nowrap text-sm">
                <Info className="w-4 h-4 mr-1.5 sm:mr-2" /> About Us
              </TabsTrigger>
              <TabsTrigger value="products" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-5 py-2 whitespace-nowrap text-sm">
                <Package className="w-4 h-4 mr-1.5 sm:mr-2" /> Our Products ({products.length})
              </TabsTrigger>
              {certificates.length > 0 && (
                <TabsTrigger value="certificates" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-5 py-2 whitespace-nowrap text-sm" data-testid="vendor-certs-tab">
                  <ShieldCheck className="w-4 h-4 mr-1.5 sm:mr-2" /> Our Certificates ({certificates.length})
                </TabsTrigger>
              )}
              <TabsTrigger value="contact" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-5 py-2 whitespace-nowrap text-sm">
                <Phone className="w-4 h-4 mr-1.5 sm:mr-2" /> Contact Us
              </TabsTrigger>
              <TabsTrigger value="reviews" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white px-3 sm:px-5 py-2 whitespace-nowrap text-sm" data-testid="vendor-reviews-tab">
                <Star className="w-4 h-4 mr-1.5 sm:mr-2" /> Reviews
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="about" className="mt-6">
            <SupplierAboutTab supplier={supplier} products={products} onEnquire={openEnquiry} />
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <SupplierProductsTab supplier={supplier} products={products} />
          </TabsContent>

          <TabsContent value="certificates" className="mt-6">
            <SupplierCertificatesTab supplier={supplier} certificates={certificates} />
          </TabsContent>

          <TabsContent value="contact" className="mt-6">
            <SupplierContactTab supplier={supplier} certificates={certificates} onSwitchToCerts={goToCerts} />
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <ReviewsList targetType="vendor" targetId={supplier.id} targetName={supplier.name} />
          </TabsContent>
        </Tabs>

        <RelatedVendors current={supplier} />
      </div>

      <Footer />
      <EnquiryModal open={enquiryOpen} onOpenChange={setEnquiryOpen} product={null} supplier={supplier} />
    </div>
  );
};

export default SupplierProfilePage;
